import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDestructive?: boolean;
    variant?: 'warning' | 'danger' | 'success';
    isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDestructive = false,
    variant,
    isLoading = false
}) => {
    if (!isOpen) return null;

    const currentVariant = variant || (isDestructive ? 'danger' : 'warning');

    const colors = {
        warning: {
            bg: 'bg-orange-500/10',
            text: 'text-orange-500',
            button: 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/20'
        },
        danger: {
            bg: 'bg-red-500/10',
            text: 'text-red-500',
            button: 'bg-red-600 hover:bg-red-500 shadow-red-900/20'
        },
        success: {
            bg: 'bg-green-500/10',
            text: 'text-green-500',
            button: 'bg-green-600 hover:bg-green-500 shadow-green-900/20'
        }
    };

    const config = colors[currentVariant];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-[#111827] border border-gray-800 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in duration-200 overflow-hidden">
                <div className="p-8 text-center">
                    <div className={`w-16 h-16 ${config.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <AlertTriangle className={`w-8 h-8 ${config.text}`} />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">{title}</h2>
                    <p className="text-sm text-gray-400 leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="px-8 py-6 bg-gray-900/50 border-t border-gray-800 flex gap-3">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 py-3 border border-gray-700 rounded-xl text-[13px] font-bold text-gray-400 hover:bg-gray-800 transition-all disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-1 py-3 ${config.button} text-white rounded-xl text-[13px] font-bold transition-all shadow-lg flex justify-center items-center gap-2 disabled:opacity-50`}
                    >
                        {isLoading ? <Loader2 className="animate-spin w-4 h-4" /> : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
